import { Spinner } from '@assembly-js/design-system';
import DocViewer, { DocRenderer, IConfig } from '@cyntler/react-doc-viewer';
import { DocViewerProps } from '@cyntler/react-doc-viewer/dist/DocViewer';
import { Dialog, DialogProps } from '@material-ui/core';
import React from 'react';

import { FileUtils } from 'src/utils';

import { AudioRenderer } from './AudioRenderer';
import { ClickAwayContext } from './context';
import { CsvRenderer } from './CsvRenderer';
import { FilePreviewHeader } from './FilePreviewHeader';
import { ImageRenderer } from './ImageRenderer';
import { NoPreviewAvailable } from './NoPreviewAvailable';
import { OctetStreamRenderer } from './OctetSteamRenderer';
import { PdfRenderer } from './PdfRenderer';
import TXTRenderer from './TxtRenderer';
import { VideoRenderer } from './VideoRenderer';

const PluginRenderers: DocRenderer[] = [
  PdfRenderer,
  VideoRenderer,
  ImageRenderer,
  CsvRenderer,
  AudioRenderer,
  TXTRenderer,
  OctetStreamRenderer,
];

function useConfig({
  onClose,
  handleDownload,
  handleNoPreview,
  fileHasNoPreview,
  handleGoToFilesTab,
}: {
  onClose: DialogProps['onClose'];
  handleDownload: () => void;
  handleNoPreview: () => void;
  fileHasNoPreview: boolean;
  handleGoToFilesTab?: () => void;
}): React.MutableRefObject<IConfig | undefined> {
  // Config for DocViewer. Defines custom renderers for different file types.
  // Defined as a ref to prevent unnecessary re-creation on each render,
  // avoiding redundant re-renders of the DocViewer component.
  return React.useRef<DocViewerProps['config']>({
    noRenderer: {
      overrideComponent: (props) => (
        <NoPreviewAvailable
          fileType={props.document?.fileType ?? ''}
          onDownloadClicked={handleDownload}
          onNoPreview={handleNoPreview}
        />
      ),
    },
    header: {
      overrideComponent: (props) => (
        <FilePreviewHeader
          onClose={() => onClose?.({}, 'backdropClick')}
          onDownload={!fileHasNoPreview ? handleDownload : undefined}
          handleGoToFilesTab={handleGoToFilesTab}
          {...props}
        />
      ),
    },
    loadingRenderer: {
      overrideComponent: () => (
        <div className="w-full h-screen flex justify-center items-center text-white">
          <Spinner size={10} />
        </div>
      ),
    },
  });
}

export const FilePreviewDialog: React.FC<
  DialogProps & {
    documents: DocViewerProps['documents'];
    handleGoToFilesTab?: () => void;
  }
> = React.memo(({ documents, onClose, handleGoToFilesTab, ...dialogProps }) => {
  const [fileHasNoPreview, setFileHasNoPreview] = React.useState(false);
  const handleNoPreview = () => {
    setFileHasNoPreview(true);
  };
  const handleDownload = async () => {
    const currentDocument = documents.at(0);
    if (currentDocument?.uri) {
      const fileName = currentDocument.fileName || '';
      try {
        await FileUtils.downloadFileFromUrl(
          currentDocument.uri,
          fileName,
          fileName,
        );
      } catch (error) {
        console.error('Error downloading file from preview', error);
      }
    }
  };

  const clickAwayContextValue = React.useMemo(
    () => ({
      onClickAway: () => {
        onClose?.({}, 'backdropClick');
      },
    }),
    [onClose],
  );

  const config = useConfig({
    onClose,
    handleDownload,
    handleNoPreview,
    fileHasNoPreview,
    handleGoToFilesTab,
  });

  return (
    <Dialog
      {...dialogProps}
      fullScreen
      BackdropProps={{
        className: 'bg-black/80',
      }}
      classes={{
        paper: 'bg-transparent scrollbar-light',
      }}
      onClose={onClose}
    >
      <ClickAwayContext.Provider value={clickAwayContextValue}>
        <DocViewer
          style={{
            backgroundColor: 'transparent',
            height: 'fit-content',
            width: '100%',
          }}
          documents={documents}
          prefetchMethod="GET"
          pluginRenderers={PluginRenderers}
          config={config.current}
        />
      </ClickAwayContext.Provider>
    </Dialog>
  );
});
